"use client";

import { useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import {
  ArrowDownWideNarrow,
  Clapperboard,
  LayoutGrid,
  Plus,
  Search,
  SlidersHorizontal,
  X,
} from "lucide-react";
import { ProjectCard, type ProjectCardProps } from "@/components/project-card";
import { stageRank, type ProjectStage } from "@/lib/project-stats";

export interface ProjectListItem extends ProjectCardProps {
  /** 排序用：更新时间戳（毫秒） */
  _updatedMs: number;
  _createdMs: number;
}

/**
 * 项目卡栅格 —— **唯一事实源**（2026-10-07 视觉改版，同日三次调整列数档位）。
 *
 * ## 列数演进：3 → 4/5 → 6@1800 → 6@1536 → **6@xl(1280)**
 *
 * 6 列两次上调档位的教训（2026-10-07 用户两次反馈「还是没变 6 列」）：
 *   - 第一次挂 `min-[1800px]`：用户 1920 物理 × 125% 缩放，视口 < 1800，够不到
 *   - 第二次挂 `min-[1536px]`：浏览器窗口非全屏/系统缩放微差，视口落在 1280~1535
 *     （截图仍是 4 列档实锤），还是够不到
 * **不要再猜用户的 CSS 视口**——桌面端 xl(1280) 起直接 6 列，任何 ≥1280 的
 * 视口必然命中。1280@6列 卡宽 190px，标题每行约 8 字（line-clamp-2 + tooltip
 * 兜底），密度优先——这是用户对「一屏多看卡」的显式取舍。
 *
 * ## 🔴 为什么 6 列必须**同时**把指标区从 3 列改成 2 列
 *
 * 一格要放下「图标 10px + gap 4px + 数字 ~24px + gap 4px + 单位『个分镜』~30px」
 * ≈ **72px**。1280@6列 + 指标3列 → 每格仅 (170-16-12)/3 = 47px，必折行；
 * 切 2 列 → 每格 74px ✓。列数与指标列数**必须联动**，否则「松散」换「拥挤」。
 * （详见 project-card.tsx 指标区注释；门禁有联动断言钉死两处断点一致。）
 *
 * ## 为什么用固定列数而不是 `auto-fill` + `minmax()`
 *
 * `repeat(auto-fill, minmax(260px, 1fr))` 在 2560 宽下会算出 8~9 列 ——
 * 卡片被压到放不下内容，整排卡片高度参差。**上限必须显式给定**。
 *
 * 移动端（<640）单列：卡片带 16:9 封面，两列会让封面小到看不清主体。
 */
export const PROJECT_GRID =
  "grid grid-cols-1 items-stretch gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6";

/**
 * 阶段筛选分组 —— 为什么按阶段而不是按 status：
 *   库里17 个项目 status 全是 draft，按 status 筛只有「全部 / 草稿」两档，等于没筛。
 *   按内容推导的阶段分，「在做的 / 做完了 / 还没开坑」才是用户脑子里真正的分类。
 *   详见 lib/project-stats.ts 顶部说明。
 */
type StageFilter = "all" | "active" | ProjectStage;

type SortKey = "updated" | "created" | "title" | "progress";

export function ProjectGrid({
  projects,
  createSlot,
}: {
  projects: ProjectListItem[];
  /** 「新建项目」触发器，由列表层渲染一次（页头/空态都用它） */
  createSlot: React.ReactNode;
}) {
  const t = useTranslations("dashboard");
  const [query, setQuery] = useState("");
  const [stage, setStage] = useState<StageFilter>("all");
  const [sort, setSort] = useState<SortKey>("updated");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = projects.filter((p) => {
      if (q && !p.title.toLowerCase().includes(q) && !(p.artStyle ?? "").toLowerCase().includes(q)) {
        return false;
      }
      if (stage === "all") return true;
      // 「进行中」= 除已成片/未开坑之外都算，即真正有活要干的那批
      if (stage === "active") return p.stage !== "done" && p.stage !== "empty";
      return p.stage === stage;
    });

    const sorted = [...list];
    sorted.sort((a, b) => {
      switch (sort) {
        case "created":
          return b._createdMs - a._createdMs;
        case "title":
          return a.title.localeCompare(b.title, "zh-Hans-CN");
        case "progress":
          return (
            b.videoProgress - a.videoProgress ||
            stageRank(b.stage) - stageRank(a.stage)
          );
        default:
          return b._updatedMs - a._updatedMs;
      }
    });
    return sorted;
  }, [projects, query, stage, sort]);

  const hasFilter = query.trim() !== "" || stage !== "all" || sort !== "updated";

  const stageFilters: { key: StageFilter; label: string; count: number }[] = [
    { key: "all", label: t("filterAll"), count: projects.length },
    {
      key: "active",
      label: t("filterActive"),
      count: projects.filter((p) => p.stage !== "done" && p.stage !== "empty").length,
    },
    ...(["done", "producing", "storyboard", "empty"] as ProjectStage[]).map((s) => ({
      key: s as StageFilter,
      label: t(`stage.${s}`),
      count: projects.filter((p) => p.stage === s).length,
    })),
  ];

  if (projects.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-[var(--border-subtle)] bg-white/50 px-6 py-20">
        <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/15 to-accent/10">
          <Clapperboard className="h-7 w-7 text-primary" />
        </div>
        <h3 className="font-display text-lg font-semibold text-[var(--text-primary)]">{t("title")}</h3>
        <p className="mt-2 max-w-sm text-center text-sm text-[var(--text-secondary)]">
          {t("noProjects")}
        </p>
        <div className="mt-6">{createSlot}</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* ── 工具条 ──
          原版只有一排裸卡片，没有任何筛选/搜索。17 个项目（含「001」「004」「测试002」
          这类同名近名）时，想找特定项目只能逐行扫标题。

          🔴 窄屏必须让筛选条**独占一行**：它有 7 个档位（全部/进行中/6 个阶段），
          与搜索框、排序挤在同一行时，实测 390px 下整条被压到只剩筛选图标、
          一个档位都点不到。所以搜索+排序是一行，筛选整条换行到下面并可横向滚动。*/}
      <div className="space-y-2 rounded-2xl border border-[var(--border-subtle)] bg-white px-3 py-2.5">
        <div className="flex flex-wrap items-center gap-2">
          {/* 搜索 */}
          <div className="relative min-w-[150px] flex-1 sm:max-w-[260px]">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--text-muted)]" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("searchPlaceholder")}
              aria-label={t("searchPlaceholder")}
              data-project-search
              className="h-8 w-full rounded-lg border border-[var(--border-subtle)] bg-white pl-8 pr-7 text-[13px] text-[var(--text-primary)] outline-none transition-colors placeholder:text-[var(--text-muted)] focus:border-primary/50"
            />
            {query ? (
              <button
                type="button"
                onClick={() => setQuery("")}
                aria-label={t("clearSearch")}
                className="absolute right-1.5 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded text-[var(--text-muted)] transition-colors hover:bg-[var(--surface)] hover:text-[var(--text-primary)]"
              >
                <X className="h-3 w-3" />
              </button>
            ) : null}
          </div>

          {/* 排序 */}
          <div className="relative shrink-0">
            <ArrowDownWideNarrow className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--text-muted)]" />
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as SortKey)}
              aria-label={t("sortLabel")}
              data-project-sort
              className="h-8 appearance-none rounded-lg border border-[var(--border-subtle)] bg-white pl-7 pr-7 text-xs text-[var(--text-secondary)] outline-none transition-colors hover:border-primary/40 focus:border-primary/50"
            >
              <option value="updated">{t("sortUpdated")}</option>
              <option value="created">{t("sortCreated")}</option>
              <option value="progress">{t("sortProgress")}</option>
              <option value="title">{t("sortTitle")}</option>
            </select>
          </div>

          {/* 可见计数：筛选后必须能立刻知道「17 个里只剩 3 个」 */}
          <span className="ml-auto shrink-0 text-xs tabular-nums text-[var(--text-muted)]">
            {filtered.length === projects.length
              ? t("countAll", { count: projects.length })
              : t("countFiltered", { count: filtered.length, total: projects.length })}
          </span>
        </div>

        {/* 阶段筛选：独占一行 + 横向滚动，窄屏也保证每个档位都够得着 */}
        <div className="-mx-1 flex items-center gap-1 overflow-x-auto px-1">
          <SlidersHorizontal className="h-3.5 w-3.5 shrink-0 text-[var(--text-muted)]" />
          {stageFilters.map((f) => {
            // 0 项的档位不置灰：一旦当前就选中它，把零结果直接呈现出来比灰掉更有信息
            const active = stage === f.key;
            return (
              <button
                key={f.key}
                type="button"
                onClick={() => setStage(f.key)}
                aria-pressed={active}
                data-stage-filter={f.key}
                className={`flex shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs transition-colors ${
                  active
                    ? "bg-primary/10 font-medium text-primary"
                    : "text-[var(--text-secondary)] hover:bg-[var(--surface)]"
                }`}
              >
                {f.label}
                <span
                  className={`rounded px-1 text-[10px] tabular-nums ${
                    active ? "bg-primary/15 text-primary" : "bg-[var(--surface)] text-[var(--text-muted)]"
                  }`}
                >
                  {f.count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* 空态必须给出路：只有「没有结果」四个字的话，用户只能自己猜该改哪个条件 */}
      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-[var(--border-subtle)] bg-white/50 px-6 py-16">
          <LayoutGrid className="h-6 w-6 text-[var(--text-muted)]" />
          <p className="mt-3 text-sm text-[var(--text-secondary)]">{t("filterEmpty")}</p>
          <button
            type="button"
            onClick={() => {
              setQuery("");
              setStage("all");
              setSort("updated");
            }}
            className="mt-3 flex items-center gap-1.5 rounded-lg border border-primary/40 bg-primary/5 px-3 py-1.5 text-xs font-medium text-primary transition-colors hover:bg-primary/10"
          >
            <Plus className="h-3 w-3" />
            {t("resetFilters")}
          </button>
        </div>
      ) : (
        <div className={PROJECT_GRID} data-project-grid>
          {filtered.map((p) => (
            <ProjectCard key={p.id} {...p} />
          ))}
        </div>
      )}
    </div>
  );
}
